import { supabase } from "../lib/supabase";

/**
 * Estate service - handles all estate-related operations
 */
export const estatesService = {
  /**
   * Create a new estate (for estate managers)
   * @param {Object} estateData - Estate information
   * @param {string} estateData.name - Estate name (required)
   * @param {string} estateData.location - City or suburb (optional)
   * @param {string} ownerUserId - The user ID who will own this estate
   */
  async create(estateData, ownerUserId) {
    try {
      const { data, error } = await supabase
        .from("estates")
        .insert([{
          name: estateData.name,
          location: estateData.location || null,
          owner_user_id: ownerUserId,
          unit_count: estateData.unit_count ?? null,
          subscription_status: estateData.subscription_status ?? 'trial',
          subscription_tier: estateData.subscription_tier ?? null,
          trial_starts_at: estateData.trial_starts_at ?? null,
          trial_ends_at: estateData.trial_ends_at ?? null,
          // Set default boundary coordinates (can be updated later)
          min_lat: -34.0,
          max_lat: -33.8,
          min_lng: 18.3,
          max_lng: 18.5,
        }])
        .select()
        .single();

      if (error) throw error;
      
      console.log('✅ Estate created successfully:', data);
      return data;
    } catch (error) {
      console.error("❌ Error creating estate:", error);
      throw error;
    }
  },

  /**
   * Check if a user already owns an estate
   * @param {string} userId - User ID to check
   */
  async userOwnsEstate(userId) {
    try {
      const { data, error } = await supabase
        .from("estates")
        .select("id")
        .eq("owner_user_id", userId)
        .limit(1);

      if (error) throw error;
      return data && data.length > 0;
    } catch (error) {
      console.error("Error checking estate ownership:", error);
      return false;
    }
  },

  /**
   * Get estate owned by a user
   * @param {string} userId - Owner user ID
   */
  async getByOwner(userId) {
    try {
      const { data, error } = await supabase
        .from("estates")
        .select("*")
        .eq("owner_user_id", userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned - user doesn't own an estate
          return null;
        }
        throw error;
      }
      return data;
    } catch (error) {
      console.error("Error fetching estate by owner:", error);
      throw error;
    }
  },

  /**
   * Get all estates
   */
  async getAll() {
    try {
      const { data, error } = await supabase
        .from("estates")
        .select("*")
        .order("name");

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching estates:", error);
      throw error;
    }
  },

  /**
   * Get a single estate by ID
   */
  async getById(estateId) {
    try {
      const { data, error } = await supabase
        .from("estates")
        .select("*")
        .eq("id", estateId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching estate:", error);
      throw error;
    }
  },

  /**
   * Get units for a specific estate
   */
  async getUnits(estateId) {
    try {
      const { data, error } = await supabase
        .from("units")
        .select("*")
        .eq("estate_id", estateId)
        .order("unit_number");

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching estate units:", error);
      throw error;
    }
  },

  /**
   * Get estate that contains given coordinates
   */
  async getEstateByCoordinates(lat, lng) {
    try {
      const { data, error } = await supabase
        .from("estates")
        .select("*")
        .lte("min_lat", lat)
        .gte("max_lat", lat)
        .lte("min_lng", lng)
        .gte("max_lng", lng)
        .limit(1);

      if (error) {
        console.error("Error finding estate by coordinates:", error);
        return null;
      }
      
      // Return first result or null if no estates found
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error("Error finding estate by coordinates:", error);
      return null;
    }
  },

  /**
   * Get reports for a specific estate
   */
  async getEstateReports(estateId, filters = {}) {
    try {
      let query = supabase
        .from("reports")
        .select("*")
        .eq("estate_id", estateId);

      // Apply filters
      if (filters.status) {
        query = query.eq("status", filters.status);
      }

      if (filters.unit_id) {
        query = query.eq("unit_id", filters.unit_id);
      }

      if (filters.start_date) {
        query = query.gte("created_date", filters.start_date);
      }

      if (filters.end_date) {
        query = query.lte("created_date", filters.end_date);
      }

      query = query.order("created_date", { ascending: false }).limit(100);

      const { data: reports, error } = await query;

      if (error) throw error;

      // Fetch related data separately to avoid 406 errors
      if (reports && reports.length > 0) {
        // Get unique unit IDs and user IDs
        const unitIds = [...new Set(reports.map(r => r.unit_id).filter(Boolean))];
        const userIds = [...new Set(reports.map(r => r.created_by).filter(Boolean))];

        // Fetch units
        let unitsMap = {};
        if (unitIds.length > 0) {
          const { data: units } = await supabase
            .from("units")
            .select("id, unit_number")
            .in("id", unitIds);
          if (units) {
            unitsMap = Object.fromEntries(units.map(u => [u.id, u]));
          }
        }

        // Fetch users
        let usersMap = {};
        if (userIds.length > 0) {
          const { data: users } = await supabase
            .from("users")
            .select("id, email, full_name")
            .in("id", userIds);
          if (users) {
            usersMap = Object.fromEntries(users.map(u => [u.id, u]));
          }
        }

        // Attach related data to reports
        return reports.map(report => ({
          ...report,
          unit: report.unit_id ? unitsMap[report.unit_id] : null,
          created_by_user: report.created_by ? usersMap[report.created_by] : null,
        }));
      }

      return reports || [];
    } catch (error) {
      console.error("Error fetching estate reports:", error);
      throw error;
    }
  },

  /**
   * Get estate statistics
   */
  async getEstateStats(estateId) {
    try {
      // Get all reports for this estate - use minimal columns to avoid errors
      const { data: reports, error } = await supabase
        .from("reports")
        .select("id, status, unit_id")
        .eq("estate_id", estateId);

      if (error) {
        console.error("Stats query error:", error);
        // Return empty stats if query fails
        return {
          totalOpen: 0,
          newThisMonth: 0,
          resolvedThisMonth: 0,
          averageResolutionTime: 0,
          topUnits: {},
          monthlyTrend: {},
        };
      }

      // If no reports, return empty stats
      if (!reports || reports.length === 0) {
        return {
          totalOpen: 0,
          newThisMonth: 0,
          resolvedThisMonth: 0,
          averageResolutionTime: 0,
          topUnits: {},
          monthlyTrend: {},
        };
      }

      // Calculate basic statistics without date fields for now
      const stats = {
        totalOpen: reports.filter(r => r.status !== "resolved").length,
        newThisMonth: reports.length, // Show total count as "new this month" 
        resolvedThisMonth: reports.filter(r => r.status === "resolved").length,
        averageResolutionTime: 0, // Can't calculate without dates
        topUnits: {},
        monthlyTrend: {},
      };

      // Top units by report count
      reports.forEach(report => {
        if (report.unit_id) {
          stats.topUnits[report.unit_id] = (stats.topUnits[report.unit_id] || 0) + 1;
        }
      });

      return stats;
    } catch (error) {
      console.error("Error fetching estate stats:", error);
      throw error;
    }
  },

  /**
   * Upload an estate logo and save its URL to the estate record.
   * @param {string} estateId - Estate ID
   * @param {File} file - Image file to upload
   * @returns {string} Public URL of the uploaded logo
   */
  async uploadLogo(estateId, file) {
    // Validate file type
    if (!file.type.startsWith("image/")) {
      throw new Error("Please upload an image file (PNG, JPG, SVG, etc.)");
    }
    // Cap file size at 2 MB
    if (file.size > 2 * 1024 * 1024) {
      throw new Error("Image must be smaller than 2 MB");
    }

    const ext = file.name.split(".").pop();
    const path = `${estateId}/logo.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("estate-logos")
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage
      .from("estate-logos")
      .getPublicUrl(path);

    // Append timestamp so the browser never serves a stale cached version
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

    const { data: updated, error: updateError } = await supabase
      .from("estates")
      .update({ logo_url: publicUrl })
      .eq("id", estateId)
      .select("id");

    if (updateError) throw updateError;
    if (!updated || updated.length === 0) throw new Error("Could not save logo — permission denied. Please refresh and try again.");

    return publicUrl;
  },

  /**
   * Export estate reports to CSV
   */
  async exportToCSV(estateId) {
    try {
      // Use the same method as getEstateReports to fetch data
      const reports = await this.getEstateReports(estateId);

      // Create CSV content
      const headers = [
        "Report ID",
        "Unit Number",
        "Category",
        "Description",
        "Status",
        "Assigned To",
        "Created At",
        "Resolved At",
        "Reported By (Name)",
        "Reported By (Email)"
      ];

      const rows = reports.map(report => [
        report.id,
        report.unit?.unit_number || "N/A",
        report.category?.replace(/_/g, " ") || "N/A",
        report.description,
        report.status,
        report.assigned_to || "N/A",
        report.created_date,
        report.resolved_date || "N/A",
        report.created_by_user?.full_name || "N/A",
        report.created_by_user?.email || "N/A"
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `estate_reports_${estateId}_${new Date().toISOString()}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return true;
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      throw error;
    }
  },

  /**
   * Get internal issues for a specific estate
   */
  async getInternalIssues(estateId, filters = {}) {
    try {
      let query = supabase
        .from("reports")
        .select("*")
        .eq("estate_id", estateId)
        .eq("is_internal", true);

      // Apply filters
      if (filters.status && filters.status !== "all") {
        query = query.eq("status", filters.status);
      }

      if (filters.assigned_to && filters.assigned_to !== "all") {
        if (filters.assigned_to === "unassigned") {
          query = query.is("assigned_to", null);
        } else {
          query = query.eq("assigned_to", filters.assigned_to);
        }
      }

      if (filters.category && filters.category !== "all") {
        query = query.eq("category", filters.category);
      }

      if (filters.start_date) {
        query = query.gte("created_date", filters.start_date);
      }

      if (filters.end_date) {
        query = query.lte("created_date", filters.end_date);
      }

      // Always order by created_date desc
      query = query.order("created_date", { ascending: false }).limit(100);

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching internal issues:", error);
      throw error;
    }
  },

  /**
   * Get internal issues statistics for a specific estate
   */
  async getInternalIssuesStats(estateId) {
    try {
      const { data: issues, error } = await supabase
        .from("reports")
        .select("*")
        .eq("estate_id", estateId)
        .eq("is_internal", true);

      if (error) throw error;

      if (!issues || issues.length === 0) {
        return {
          totalOpen: 0,
          newThisMonth: 0,
          resolvedThisMonth: 0,
          avgResolutionTime: 0,
          byCategory: {},
          byAssignment: {},
          monthlyTrend: {}
        };
      }

      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const totalOpen = issues.filter(i => i.status !== "resolved").length;
      const newThisMonth = issues.filter(i => new Date(i.created_date) >= thisMonth).length;
      const resolvedThisMonth = issues.filter(i => 
        i.status === "resolved" && 
        i.resolved_date && 
        new Date(i.resolved_date) >= thisMonth
      ).length;

      // Calculate average resolution time
      const resolvedIssues = issues.filter(i => i.status === "resolved" && i.resolved_date);
      const avgResolutionTime = resolvedIssues.length > 0
        ? resolvedIssues.reduce((acc, issue) => {
            const created = new Date(issue.created_date);
            const resolved = new Date(issue.resolved_date);
            return acc + (resolved - created) / (1000 * 60 * 60 * 24); // days
          }, 0) / resolvedIssues.length
        : 0;

      // Group by category
      const byCategory = issues.reduce((acc, issue) => {
        acc[issue.category] = (acc[issue.category] || 0) + 1;
        return acc;
      }, {});

      // Group by assignment
      const byAssignment = issues.reduce((acc, issue) => {
        const assignment = issue.assigned_to || "unassigned";
        acc[assignment] = (acc[assignment] || 0) + 1;
        return acc;
      }, {});

      // Monthly trend (last 6 months)
      const monthlyTrend = {};
      for (let i = 5; i >= 0; i--) {
        const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
        const key = month.toISOString().slice(0, 7); // YYYY-MM format
        
        monthlyTrend[key] = issues.filter(i => {
          const created = new Date(i.created_date);
          return created >= month && created < nextMonth;
        }).length;
      }

      return {
        totalOpen,
        newThisMonth,
        resolvedThisMonth,
        avgResolutionTime: Math.round(avgResolutionTime * 10) / 10, // round to 1 decimal
        byCategory,
        byAssignment,
        monthlyTrend
      };
    } catch (error) {
      console.error("Error fetching internal issues stats:", error);
      throw error;
    }
  },

  // ─── Phase 1.5: Multi-Estate Methods ─────────────────────────────────────

  /**
   * Get all estates a user is a member of (via estate_members join table).
   * Returns estates sorted by name.
   */
  async getMyEstates(userId) {
    try {
      const { data, error } = await supabase
        .from("estate_members")
        .select("role, estate:estates(*)")
        .eq("user_id", userId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      // Flatten: return array of { ...estate, member_role }
      return (data || []).map(row => ({ ...row.estate, member_role: row.role }));
    } catch (error) {
      console.error("Error fetching user estates:", error);
      throw error;
    }
  },

  /**
   * Add or update a member on an estate.
   * Uses upsert so it's safe to call if membership already exists.
   */
  async addMember(estateId, userId, role = "manager") {
    try {
      const { error } = await supabase
        .from("estate_members")
        .upsert({ user_id: userId, estate_id: estateId, role }, { onConflict: "user_id,estate_id" });
      if (error) throw error;
    } catch (error) {
      console.error("Error adding estate member:", error);
      throw error;
    }
  },

  /**
   * Remove a member from an estate.
   */
  async removeMember(estateId, userId) {
    try {
      const { error } = await supabase
        .from("estate_members")
        .delete()
        .eq("estate_id", estateId)
        .eq("user_id", userId);
      if (error) throw error;
    } catch (error) {
      console.error("Error removing estate member:", error);
      throw error;
    }
  },

  /**
   * Get all members of an estate with their user profile details.
   * Returns array of { user_id, role, full_name, email, profile_photo }.
   */
  async getEstateMembers(estateId) {
    try {
      const { data, error } = await supabase
        .from("estate_members")
        .select("user_id, role, user:users(full_name, email, profile_photo)")
        .eq("estate_id", estateId);
      if (error) throw error;
      return (data || []).map(row => ({
        user_id: row.user_id,
        role: row.role,
        full_name: row.user?.full_name || row.user?.email || "Manager",
        email: row.user?.email,
        profile_photo: row.user?.profile_photo || null,
      }));
    } catch (error) {
      console.error("Error fetching estate members:", error);
      throw error;
    }
  },

  /**
   * Create an invite code for another manager to join an estate.
   * Code expires in 72 hours.
   * Returns the full invite URL to copy/share.
   */
  async createInvite(estateId, invitedByUserId) {
    try {
      // Generate a random 10-char alphanumeric code
      const code = Array.from(crypto.getRandomValues(new Uint8Array(8)))
        .map(b => b.toString(36).padStart(2, "0"))
        .join("")
        .slice(0, 10)
        .toUpperCase();

      const expiresAt = new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString();

      const { error } = await supabase
        .from("estate_invites")
        .insert({ estate_id: estateId, invited_by: invitedByUserId, code, expires_at: expiresAt });

      if (error) throw error;

      const inviteUrl = `${window.location.origin}/JoinEstate?code=${code}`;
      return { code, inviteUrl, expiresAt };
    } catch (error) {
      console.error("Error creating estate invite:", error);
      throw error;
    }
  },

  /**
   * Look up an invite's estate info without accepting it (for the preview screen).
   */
  async getInviteInfo(code) {
    try {
      const { data, error } = await supabase
        .from("estate_invites")
        .select("id, code, expires_at, accepted_at, estate:estates(id, name, location), inviter:users!estate_invites_invited_by_fkey(full_name, email)")
        .eq("code", code.toUpperCase())
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching invite info:", error);
      throw error;
    }
  },

  /**
   * Permanently delete an estate owned by the current user.
   * Cascades to estate_members, estate_invites, units, reports via FK ON DELETE CASCADE.
   */
  async deleteEstate(estateId) {
    try {
      const { error, count } = await supabase
        .from("estates")
        .delete({ count: 'exact' })
        .eq("id", estateId);
      if (error) throw error;
      if (count === 0) throw new Error("Delete blocked — you may not be the owner of this estate.");
    } catch (error) {
      console.error("Error deleting estate:", error);
      throw error;
    }
  },

  /**
   * Accept an invite code: validates it then inserts the user into estate_members.
   * Returns the estate they just joined.
   */
  async acceptInvite(code, userId) {
    try {
      const invite = await this.getInviteInfo(code);

      if (!invite) throw new Error("Invite not found.");
      if (invite.accepted_at) throw new Error("This invite has already been used.");
      if (new Date(invite.expires_at) < new Date()) throw new Error("This invite has expired.");

      // Add member (upsert handles already-a-member gracefully)
      await this.addMember(invite.estate.id, userId, "manager");

      // Mark invite as accepted
      const { error } = await supabase
        .from("estate_invites")
        .update({ accepted_by: userId, accepted_at: new Date().toISOString() })
        .eq("id", invite.id);

      if (error) throw error;

      return invite.estate;
    } catch (error) {
      console.error("Error accepting invite:", error);
      throw error;
    }
  },
};
